import { createApi } from '@reduxjs/toolkit/query/react';
import { pubUrl } from '@findemdev/findem-helpers';
import customBaseQuery from './baseQuery';
import { CompanyData, IGetCompanyDetailsParams, IGetCompanyDetailsResponse, IGetCompanyLogosParams, IGetCompanyLogosResponse } from '../types/pub';
import { sanitizeProfiles } from '../utils/common';
import { IProfile } from '../types/profile';
import { ICompanyList } from '../components/CompanyBuilder/types/common';
import { IGetAllCompanyListsParams } from '../components/CompanyBuilder/types/api';
import { IPlatformSettings } from '../types/agent-pipelines';

export interface ICapiqCompanyData {
  company_id?: string;
  org_type_raw?: string;
}

interface ICompanyParams {
  meta_refs: string[];
  competitors_refs?: number;
  joid?: string;
  send_findem_data?: boolean;
}

interface IResponse {
  data: CompanyData;
}

interface IProfileParams {
  joid: string;
  profile_ids: string[];
  detail?: string;
  sandbox?: boolean;
  bust_cache?: boolean;
  is_rra?: boolean;
}

interface IPlatformSettingsParams {
  joid?: string;
  settingsName: string;
}



export const pubApi = createApi({
  reducerPath: 'pub',
  baseQuery: (args, api, extraOptions) =>
    customBaseQuery(args, api, extraOptions, pubUrl),
  tagTypes: ['getProfiles'],
  endpoints: (build) => ({
    getCompany: build.query<CompanyData, ICompanyParams>({
      query: ({ meta_refs, competitors_refs: _competitors_refs, joid, send_findem_data }) => {
        const url = `pub/api/company_details?meta_refs=${meta_refs}${joid ? `&joid=${joid}` : ''}${send_findem_data ? `&send_findem_data=true` : ''}`;
        return {
          url,
          credentials: 'include',
          method: 'GET'
        };
      },
      transformResponse: (response: IResponse) =>
        response.data || { companyListDetails: [], edgarFinancialInfo: [] }
    }),
    getCompanyLogosByMetaRefs: build.query<IGetCompanyLogosResponse, IGetCompanyLogosParams>({
      query: ({ metaRefs, joid }) => {
        const params = new URLSearchParams({ joid });

        return {
          url: `pub/api/company_details?${params.toString()}`,
          credentials: 'include',
          method: 'POST',
          body: {
            meta_refs: metaRefs,
            projection: {
              "company.logos": 1,
              "company.company_name": 1,
            },
          }
        }
      }
    }),
    getCompanyDetails: build.query<IGetCompanyDetailsResponse, IGetCompanyDetailsParams>({
      query: ({ metaRefs, joid, projection, recentOnlyFields }) => {
        const params = new URLSearchParams({ joid });

        return {
          url: `pub/api/company_details?${params.toString()}`,
          credentials: 'include',
          method: 'POST',
          body: {
            meta_refs: metaRefs,
            projection,
            recent_only_fields: recentOnlyFields
          }
        }
      }
    }),
    getProfiles: build.query<Record<string, IProfile>, IProfileParams>({
      keepUnusedDataFor: 60 * 1000,
      query: (body) => {
        // BE Doesn't reject this ATM but if you're requesting for more than 1K
        // profiles, either paginate or seek alternative solution for request size.
        if (body.profile_ids.length > 1000) {
          console.error("API Error: Too Many PRIDs");
        }
        return {
          url: `pub/api/profile?${new URLSearchParams({ joid: body.joid }).toString()}`,
          credentials: "include",
          method: "POST",
          body: {
            detail: body.detail ? body.detail : "true",
            joid: body.joid,
            prids: body.profile_ids,
            bust_cache: body.bust_cache ? body.bust_cache : false,
            type: "Profiles",
            ...(body?.is_rra && { is_rra: true }),
            ...(body?.sandbox && { sandbox: true }),
          },
        };
      },

      transformResponse: (response: Record<string, IProfile>, _meta, _arg) =>
        sanitizeProfiles(response),
    }),
    getPlatformSettings: build.query<IPlatformSettings, IPlatformSettingsParams>({
      query: ({ joid, settingsName }) => {
        const params = new URLSearchParams({
          setting_name: settingsName,
        });
        if (joid) {
          params.set('joid', joid);
        }

        return {
          url: `pub/api/platform_settings?${params.toString()}`,
          credentials: 'include',
          method: 'GET',
        };
      },
    }),

    // Company Builder - start
    getAllCompanyLists: build.query<ICompanyList[], IGetAllCompanyListsParams>({
      query: (params) => {
        const searchParams = new URLSearchParams({
          joid: params.joid,
        });

        return {
          url: `pub/api/execmap?${searchParams.toString()}`,
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          method: 'POST',
          body: {
            type: 'fetch_all_company_lists',
            joid: params.joid,
          }
        }
      }
    }),
    // Company Builder - end
  }),

});

export const {
  endpoints,
  useGetCompanyQuery,
  useGetProfilesQuery,
  useGetCompanyDetailsQuery,
  useLazyGetCompanyDetailsQuery,
  useLazyGetCompanyLogosByMetaRefsQuery,
  useLazyGetProfilesQuery,
  useGetCompanyLogosByMetaRefsQuery,
  useGetPlatformSettingsQuery,
  // Company Builder - start
  useGetAllCompanyListsQuery,
  // Company Builder - end
} = pubApi;
